import { useRef, useState } from "react";
// import { useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import ReactPlayer from "react-player";
import type { OnProgressProps } from "react-player/base";
import Spinner from "react-bootstrap/Spinner";

// import type { RootState } from "../../../store/state/rootReducer";
import AudioFileWave from "../../../assets/svg/AudioFileWave.svg";

import ProgressBar from "./ProgressBar/TSProgressBar";
import Controls from "./Controls/TSControls";
import { formatTime } from "./utils";

interface TSVideoPlayerProps {
  videoSrc: string;
  isAudioFile: boolean;
}

const TSVideoPlayer = ({ videoSrc, isAudioFile }: TSVideoPlayerProps) => {
  const videoRef = useRef<ReactPlayer>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.6);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [played, setPlayed] = useState<number>(0);
  const [progressBarSeeking, setProgressBarSeeking] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const currentTime = videoRef && videoRef.current ? videoRef.current.getCurrentTime() : 0;
  const formattedCurrentTime = formatTime(currentTime);

  const duration = videoRef && videoRef.current ? videoRef.current.getDuration() : 0;
  const formattedTotalDuration = formatTime(duration);

  const handlePlayPause = () => setIsPlaying((prev) => !prev);
  const handleMute = () => setIsMuted((prev) => !prev);
  const handlePlaybackRate = (rate: number) => setPlaybackRate(rate);

  const handleVolume = (event: Event, value: number | number[]) => {
    if (typeof value === "number") {
      setVolume(value / 100);
      setIsMuted(value === 0);
    }
  };

  const handleVolumeSeekDown = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    if (typeof value === "number") {
      setVolume(value / 100);
      setIsMuted(value === 0);
    }
  };

  const handleForward = () => {
    if (videoRef?.current) {
      const timeCandidate = videoRef.current.getCurrentTime() + 5;
      const time = Math.min(videoRef.current.getDuration(), timeCandidate);
      videoRef?.current?.seekTo(time, "seconds");
    }
  };

  const handleReplay = () => {
    if (videoRef?.current) {
      const timeCandidate = videoRef.current.getCurrentTime() - 5;
      const time = timeCandidate > 0 ? timeCandidate : 0;
      videoRef?.current?.seekTo(time, "seconds");
    }
  };

  const handleProgress = (state: OnProgressProps) => {
    if (!progressBarSeeking) {
      setPlayed(state.played);
    }
  };

  const handleProgressBarSeek = (event: Event, value: number | number[]) => {
    if (typeof value === "number") {
      setPlayed(value / 100);
    }
  };
  const handleProgressBarMouseDown = () => {
    setProgressBarSeeking(true);
  };
  const handleProgressBarMouseUp = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    setProgressBarSeeking(false);
    if (typeof value === "number") {
      videoRef.current?.seekTo(value / 100, "fraction");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        paddingBottom: "0px",
        borderRadius: "5px",
        overflow: "hidden",
        margin: "0 auto",
        background: isAudioFile ? "#D9DCDE" : "black",
      }}
    >
      {!videoReady && (
        <Box sx={{ background: isAudioFile ? "#D9DCDE" : "#000", width: "100%", height: "100%" }}>
          {" "}
          <Spinner
            animation="border"
            variant="light"
            style={{ position: "absolute", top: "40%", left: "47%" }}
          ></Spinner>
        </Box>
      )}
      <ReactPlayer
        style={{ borderRadius: "5px", overflow: "hidden" }}
        width="100%"
        height="100%"
        ref={videoRef}
        url={videoSrc}
        playing={isPlaying}
        muted={isMuted}
        playbackRate={playbackRate}
        onProgress={handleProgress}
        volume={volume}
        // progressInterval={500}
        controls={false}
        playsinline
        onReady={() => setVideoReady(true)}
      />
      {isAudioFile && videoReady && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            bottom: "54px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={AudioFileWave} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "absolute",
          bottom: 0,
          right: 0,
          left: 0,
          width: "100%",
          background: isAudioFile
            ? "rgba(142, 151, 159, 0.9)"
            : "linear-gradient(360deg, #000000 42.19%, rgba(0, 0, 0, 0) 100%)",
          zIndex: 2,
          color: "white",
        }}
      >
        <Grid container alignContent="center" sx={{ width: "100%" }}>
          <Grid item xs={12} sx={{ padding: "0px" }}>
            <ProgressBar
              value={played * 100}
              handleProgressBarSeek={handleProgressBarSeek}
              handleProgressBarMouseDown={handleProgressBarMouseDown}
              handleProgressBarMouseUp={handleProgressBarMouseUp}
              formattedCurrentTime={formattedCurrentTime}
            />
          </Grid>
        </Grid>
        <Controls
          isPlaying={isPlaying}
          isMuted={isMuted}
          playbackRate={playbackRate}
          formattedCurrentTime={formattedCurrentTime}
          formattedTotalDuration={formattedTotalDuration}
          handlePlayPause={handlePlayPause}
          handleReplay={handleReplay}
          handleForward={handleForward}
          handleMute={handleMute}
          handlePlaybackRate={handlePlaybackRate}
          volume={volume}
          handleVolume={handleVolume}
          handleVolumeSeekDown={handleVolumeSeekDown}
        />
      </Box>
    </Box>
  );
};

export default TSVideoPlayer;
